import { fields } from "components/atoms/AutoComplete";

export type GooglePlaceDetails = {
  value: string;
  details: Record<string, unknown>;
};

const getGoogle = (): typeof google.maps | null => {
  const g = (window as Window & { google?: typeof google }).google;
  return g?.maps ? g.maps : null;
};

export const waitForGoogleMaps = (): Promise<typeof google.maps> =>
  new Promise((resolve, reject) => {
    const existing = getGoogle();
    if (existing) {
      resolve(existing);
      return;
    }

    let attempts = 0;
    const maxAttempts = 100;
    const timer = window.setInterval(() => {
      const maps = getGoogle();
      attempts += 1;
      if (maps) {
        window.clearInterval(timer);
        resolve(maps);
        return;
      }
      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
        reject(new Error("Google Maps failed to load"));
      }
    }, 100);
  });

const findAddressComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
) => components?.find((c) => c.types.includes(type));

export const mapGooglePlaceToFormValue = (
  place: google.maps.places.PlaceResult,
  description: string,
): GooglePlaceDetails | null => {
  const lat = place.geometry?.location?.lat?.();
  const lng = place.geometry?.location?.lng?.();
  if (lat == null || lng == null) return null;

  const components = place.address_components || [];
  const route = findAddressComponent(components, "route");
  const streetNumber = findAddressComponent(components, "street_number");
  const postalCode = findAddressComponent(components, "postal_code");
  const country = findAddressComponent(components, "country");
  const state = findAddressComponent(
    components,
    "administrative_area_level_1",
  );
  const locality = findAddressComponent(components, "locality");

  const street = route
    ? streetNumber
      ? `${streetNumber.long_name} ${route.long_name}`
      : route.long_name
    : place.name || description.split(",")[0] || "";

  const formattedAddress = place.formatted_address || description;
  const countryCode = country?.short_name || "";

  return {
    value: formattedAddress,
    details: {
      ...place,
      name: place.name,
      title: place.name,
      address: formattedAddress,
      formatted_address: formattedAddress,
      place_id: place.place_id,
      types: place.types,
      city: locality?.long_name,
      route: route?.long_name,
      airport: place.types?.includes("airport") ?? false,
      street,
      zipcode: postalCode?.long_name || "",
      zipCode: postalCode?.long_name || "",
      country: country?.long_name || countryCode,
      country_name_code: countryCode,
      state: state?.long_name || "",
      coordinates: { lat, lng },
      geometry: {
        location: { lat, lng },
      },
      address_components: components.map((c) => ({
        types: c.types,
        long_name: c.long_name,
        short_name: c.short_name,
      })),
    },
  };
};

export const createPlacesService = (maps: typeof google.maps) => {
  let node = document.getElementById("place") as HTMLDivElement | null;
  if (!node) {
    node = document.createElement("div");
    node.id = "place";
    node.style.display = "none";
    document.body.appendChild(node);
  }
  return new maps.places.PlacesService(node);
};

export const getPlaceDetails = (
  placesService: google.maps.places.PlacesService,
  placeId: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.PlaceResult> =>
  new Promise((resolve, reject) => {
    placesService.getDetails(
      {
        placeId,
        sessionToken,
        fields: [...fields],
      },
      (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && result) {
          resolve(result);
          return;
        }
        reject(new Error(status || "Failed to load place details"));
      },
    );
  });

export const getPlacePredictions = (
  autocompleteService: google.maps.places.AutocompleteService,
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
  pickupLat?: number,
  pickupLng?: number,
): Promise<google.maps.places.AutocompletePrediction[]> =>
  new Promise((resolve, reject) => {
    const request: google.maps.places.AutocompletionRequest = {
      input,
      sessionToken,
    };

    if (
      pickupLat != null &&
      pickupLng != null &&
      !Number.isNaN(pickupLat) &&
      !Number.isNaN(pickupLng)
    ) {
      request.locationBias = {
        center: { lat: pickupLat, lng: pickupLng },
        radius: 50000,
      };
    }

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (
        status === google.maps.places.PlacesServiceStatus.OK &&
        predictions
      ) {
        resolve(predictions);
        return;
      }
      if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([]);
        return;
      }
      reject(new Error(status || "Failed to fetch place suggestions"));
    });
  });
